import React, { useContext, useMemo, useState } from "react";
import ProjectList from "./ProjectList";
import {
  Grid, Paper, Typography, Box,
  TextField, InputAdornment, Tabs, Tab,
} from "@mui/material";
import {
  RocketLaunch, Search,
  Assignment, Sort, PlayArrow, DoneAll,
} from "@mui/icons-material";
import { ProjectContext } from "../../../AppContext/ProjectContext";
import { getWeight } from "../../constants";

const App = () => {
  const { allProjects } = useContext(ProjectContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [sortBy, setSortBy] = useState("newest");

  // Status-based filtering
  const openProjects = useMemo(
    () => allProjects.filter((t) => t.status === "open"),
    [allProjects]
  );
  const inProgressProjects = useMemo(
    () => allProjects.filter((t) => t.status === "in-progress"),
    [allProjects]
  );
  const resolvedProjects = useMemo(
    () => allProjects.filter((t) => t.status === "resolved" || t.status === "closed"),
    [allProjects]
  );

  const filteredProjects = useMemo(() => {
    const sources = [allProjects, openProjects, inProgressProjects, resolvedProjects];
    const source = sources[tabValue] || allProjects;
    let result = source;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.title?.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          String(t.projectNumber)?.includes(q)
      );
    }
    // Sort
    const sorted = [...result];
    switch (sortBy) {
      case "priority":
        sorted.sort((a, b) => getWeight(b) - getWeight(a));
        break;
      case "oldest":
        sorted.sort((a, b) => {
          const aTime = a.timestamp?.seconds || a.timestamp?.toDate?.()?.getTime() / 1000 || 0;
          const bTime = b.timestamp?.seconds || b.timestamp?.toDate?.()?.getTime() / 1000 || 0;
          return aTime - bTime;
        });
        break;
      case "project":
        sorted.sort((a, b) => (b.projectNumber || 0) - (a.projectNumber || 0));
        break;
      case "newest":
      default:
        sorted.sort((a, b) => {
          const aTime = a.timestamp?.seconds || a.timestamp?.toDate?.()?.getTime() / 1000 || 0;
          const bTime = b.timestamp?.seconds || b.timestamp?.toDate?.()?.getTime() / 1000 || 0;
          return bTime - aTime;
        });
        break;
    }
    return sorted;
  }, [allProjects, openProjects, inProgressProjects, resolvedProjects, searchQuery, tabValue, sortBy]);

  return (
    <>
      {/* Search & Sort */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 1.5, sm: 2 }, borderRadius: 2 }} elevation={1}>
        <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth size="small"
              placeholder="Search by title, description, or #..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search color="action" sx={{ fontSize: { xs: 18, sm: 24 } }} /></InputAdornment>,
                sx: { fontSize: { xs: "0.8rem", sm: "1rem" } },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select fullWidth size="small"
              value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Sort color="action" sx={{ fontSize: { xs: 18, sm: 24 } }} /></InputAdornment>,
                sx: { fontSize: { xs: "0.8rem", sm: "1rem" } },
              }}
              SelectProps={{ native: true }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="priority">Highest Priority</option>
              <option value="project">Project Number</option>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: { xs: 1.5, sm: 2 }, borderRadius: 2 }} elevation={1}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="fullWidth"
          sx={{
            minHeight: { xs: 40, sm: 48 },
            "& .MuiTab-root": {
              fontWeight: "bold",
              textTransform: "none",
              fontSize: { xs: "0.65rem", sm: "0.875rem" },
              minHeight: { xs: 40, sm: 48 },
              px: { xs: 0.5, sm: 2 },
              py: { xs: 0.5, sm: 1 },
            },
            "& .MuiTab-iconWrapper": {
              fontSize: { xs: 14, sm: 20 },
              mr: { xs: 0.25, sm: 1 },
            },
          }}
        >
          <Tab icon={<Assignment sx={{ fontSize: { xs: 14, sm: 20 } }} />} iconPosition="start" label={`All (${allProjects.length})`} />
          <Tab icon={<RocketLaunch sx={{ fontSize: { xs: 14, sm: 20 } }} />} iconPosition="start" label={`Open (${openProjects.length})`} />
          <Tab icon={<PlayArrow sx={{ fontSize: { xs: 14, sm: 20 } }} />} iconPosition="start" label={`In Progress (${inProgressProjects.length})`} />
          <Tab icon={<DoneAll sx={{ fontSize: { xs: 14, sm: 20 } }} />} iconPosition="start" label={`Resolved (${resolvedProjects.length})`} />
        </Tabs>
      </Paper>

      {/* Issue List */}
      {filteredProjects.length > 0 ? (
        <ProjectList projects={filteredProjects} />
      ) : (
        <Paper sx={{ textAlign: "center", py: { xs: 4, sm: 8 }, borderRadius: 2 }}>
          <RocketLaunch sx={{ fontSize: { xs: 40, sm: 64 }, color: "#ccc", mb: 2 }} />
          <Typography sx={{ fontSize: { xs: "0.95rem", sm: "1.25rem" }, fontWeight: 500 }} color="text.secondary">
            {searchQuery
              ? "No projects match your search"
              : "No development projects yet"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? "Try adjusting your search criteria"
              : "Create your first project to get started"}
          </Typography>
        </Paper>
      )}
    </>
  );
};

export default App;
